/**
 * AgentLabPage — Brain 自动化任务管理
 *
 * - 显示所有自动化任务（List automations）
 * - 创建新任务（Create form）
 * - 手动触发运行（Manual run button）
 * - 查看运行历史（Run history）
 * - 启用 / 禁用任务（Toggle enabled）
 *
 * API endpoints:
 *   GET  /api/v1/brain/automations      — 所有任务
 *   POST /api/v1/brain/automations      — 创建任务
 *   POST /api/v1/brain/automations/:id/run — 手动触发
 *   GET  /api/v1/tenants/:id/automations/:id/runs — 运行历史
 */

import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  RefreshCw, Loader2, Play, Plus, ChevronDown, ChevronRight,
  CheckCircle2, XCircle, Clock, Zap,
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════
//  Types
// ═══════════════════════════════════════════════════════════════

interface AgentAutomation {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  skillName: string;
  schedule?: string | { type: string; intervalMs: number };
  enabled: boolean;
  lastRunAt?: string;
  nextRunAt?: string;
  createdAt: string;
}

interface AutomationRun {
  id: string;
  automationId: string;
  status: 'pending' | 'running' | 'success' | 'error';
  output?: unknown;
  error?: string;
  startedAt: string;
  completedAt?: string;
}

// ═══════════════════════════════════════════════════════════════
//  Helpers
// ═══════════════════════════════════════════════════════════════

function ageFromNow(iso: string | null | undefined): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  return `${Math.floor(min / 60)}h ago`;
}

function RunStatusBadge({ status }: { status: AutomationRun['status'] }) {
  switch (status) {
    case 'success':
      return <Badge className="gap-1 bg-emerald-600 hover:bg-emerald-700 text-xs"><CheckCircle2 className="h-3 w-3" />成功</Badge>;
    case 'error':
      return <Badge variant="destructive" className="gap-1 text-xs"><XCircle className="h-3 w-3" />失败</Badge>;
    case 'running':
      return <Badge variant="secondary" className="gap-1 text-xs"><Loader2 className="h-3 w-3 animate-spin" />运行中</Badge>;
    default:
      return <Badge variant="outline" className="text-xs"><Clock className="h-3 w-3 mr-1" />等待</Badge>;
  }
}

// ═══════════════════════════════════════════════════════════════
//  AutomationRow
// ═══════════════════════════════════════════════════════════════

function AutomationRow({
  automation,
  onRun,
  onToggle,
}: {
  automation: AgentAutomation;
  onRun: (id: string) => Promise<void>;
  onToggle: (id: string, enabled: boolean) => Promise<void>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [runs, setRuns] = useState<AutomationRun[]>([]);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [running, setRunning] = useState(false);

  const loadRuns = useCallback(async () => {
    if (!expanded) return;
    setLoadingRuns(true);
    try {
      const result = await api.get<AutomationRun[]>(`/brain/automations/${automation.id}/runs`);
      setRuns(result ?? []);
    } catch {
      // ignore
    } finally {
      setLoadingRuns(false);
    }
  }, [expanded, automation.id]);

  useEffect(() => { void loadRuns(); }, [loadRuns]);

  const handleRun = async () => {
    setRunning(true);
    try {
      await onRun(automation.id);
      if (expanded) await loadRuns();
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="border rounded-lg bg-white dark:bg-zinc-900">
      <div className="flex items-center justify-between p-4 gap-4">
        {/* Toggle expand */}
        <button
          className="flex items-center gap-2 flex-1 text-left min-w-0"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded
            ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
            : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
          }
          <div className="min-w-0">
            <p className="font-medium truncate">{automation.name}</p>
            <p className="text-xs text-muted-foreground truncate">
              <span className="font-mono bg-muted px-1 rounded">{automation.skillName}</span>
              {automation.schedule && <span className="ml-2 text-muted-foreground">{typeof automation.schedule === 'string' ? automation.schedule : `${automation.schedule.type} / ${Math.round(automation.schedule.intervalMs / 60000)}min`}</span>}
            </p>
          </div>
        </button>

        {/* Status + Actions */}
        <div className="flex items-center gap-2 shrink-0">
          <Badge variant={automation.enabled ? 'default' : 'secondary'} className="text-xs">
            {automation.enabled ? '已启用' : '已禁用'}
          </Badge>
          <span className="text-xs text-muted-foreground hidden sm:block">
            {automation.lastRunAt ? `上次: ${ageFromNow(automation.lastRunAt)}` : '未运行'}
          </span>
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1 text-xs"
            disabled={running}
            onClick={handleRun}
          >
            {running ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
            运行
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            onClick={() => void onToggle(automation.id, !automation.enabled)}
          >
            {automation.enabled ? '禁用' : '启用'}
          </Button>
        </div>
      </div>

      {/* Expanded run history */}
      {expanded && (
        <div className="border-t px-4 py-3 bg-muted/30">
          <p className="text-xs font-medium text-muted-foreground mb-2">最近运行记录</p>
          {loadingRuns && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          {!loadingRuns && runs.length === 0 && (
            <p className="text-xs text-muted-foreground">暂无运行记录</p>
          )}
          {!loadingRuns && runs.slice(0, 5).map((run) => (
            <div key={run.id} className="flex items-center gap-3 py-1.5 border-b last:border-0">
              <RunStatusBadge status={run.status} />
              <span className="text-xs text-muted-foreground">{ageFromNow(run.startedAt)}</span>
              {run.error && (
                <span className="text-xs text-destructive truncate max-w-xs">{run.error}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  Create Form
// ═══════════════════════════════════════════════════════════════

function CreateAutomationForm({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', skillName: '', schedule: '' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.skillName) return;
    setSaving(true);
    try {
      await api.post('/brain/automations', {
        name: form.name,
        skillName: form.skillName,
        schedule: form.schedule || undefined,
        enabled: true,
      });
      setForm({ name: '', skillName: '', schedule: '' });
      setOpen(false);
      onCreated();
    } catch {
      // ignore — the outer component will reload
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <Button size="sm" className="gap-1" onClick={() => setOpen(true)}>
        <Plus className="h-4 w-4" />新建自动化
      </Button>
    );
  }

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-sm">新建自动化任务</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">任务名称 *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="例：每日AR催收"
                className="h-8 text-sm"
                required
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Skill 名称 *</Label>
              <Input
                value={form.skillName}
                onChange={(e) => setForm({ ...form, skillName: e.target.value })}
                placeholder="例：ar-collection"
                className="h-8 text-sm font-mono"
                required
              />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-xs">调度周期 (可选，cron 语法)</Label>
              <Input
                value={form.schedule}
                onChange={(e) => setForm({ ...form, schedule: e.target.value })}
                placeholder="例：0 9 * * 1 (周一早9点)"
                className="h-8 text-sm font-mono"
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>取消</Button>
            <Button type="submit" size="sm" disabled={saving}>
              {saving && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
              创建
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════
//  AgentLabPage
// ═══════════════════════════════════════════════════════════════

export default function AgentLabPage() {
  const [automations, setAutomations] = useState<AgentAutomation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.get<AgentAutomation[]>('/brain/automations');
      setAutomations(result ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const handleRun = async (id: string) => {
    await api.post(`/brain/automations/${id}/run`, {});
    await load();
  };

  const handleToggle = async (id: string, enabled: boolean) => {
    await api.patch(`/brain/automations/${id}`, { enabled });
    await load();
  };

  const enabled = automations.filter((a) => a.enabled);
  const disabled = automations.filter((a) => !a.enabled);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Zap className="h-6 w-6 text-yellow-500" />
            AgentLab 自动化任务
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            管理 Brain 自动化调度任务，查看执行历史
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
          <CreateAutomationForm onCreated={() => void load()} />
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: '全部任务', value: automations.length, color: 'text-foreground' },
          { label: '已启用', value: enabled.length, color: 'text-emerald-600' },
          { label: '已禁用', value: disabled.length, color: 'text-muted-foreground' },
        ].map(({ label, value, color }) => (
          <Card key={label}>
            <CardContent className="p-4 text-center">
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
              <p className="text-xs text-muted-foreground">{label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded p-3">
          {error}
        </div>
      )}

      {/* Automation List */}
      {!loading && automations.length === 0 && !error && (
        <div className="text-center py-12 text-muted-foreground text-sm">
          暂无自动化任务。点击「新建自动化」开始创建。
        </div>
      )}

      {automations.length > 0 && (
        <div className="space-y-3">
          {enabled.map((a) => (
            <AutomationRow key={a.id} automation={a} onRun={handleRun} onToggle={handleToggle} />
          ))}
          {disabled.length > 0 && (
            <>
              <p className="text-xs text-muted-foreground pt-2">已禁用</p>
              {disabled.map((a) => (
                <AutomationRow key={a.id} automation={a} onRun={handleRun} onToggle={handleToggle} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
